import { matchRoutes } from "react-router-dom";

/**
 * The title every authenticated route shows in the page chrome.
 *
 * ## Why this table exists
 *
 * The chrome renders the `<h1>` for every authenticated route (see
 * `salt-page-chrome.tsx` for the ownership decision and who made it). That makes the
 * title the page's *only* name, so an unmapped route is no longer a cosmetic miss — it
 * is a page with no heading.
 *
 * The previous resolver was an `if` chain over eleven prefixes ending in
 * `return "Dashboard"`. MEASURED on 2026-08-18 by replaying that chain over the 88
 * paths declared inside `DashboardLayoutRoute` in `src/App.tsx`:
 *
 *   14  fell through to "Dashboard" — /strategy, /campaigns/:campaignId,
 *       /campaign-strategy/:sessionId, /rep-room, /negotiation, /account/security, …
 *   47  collapsed onto ONE string via `startsWith("/corporate/")` — every corporate
 *       sub-page, from /goals to /email-studio to /policies/templates/create, shared
 *       the corporate's name as its heading and none of them said which page it was
 *   13  collapsed onto "Admin" via `startsWith("/admin")`
 *
 * So 74 of 88 named something other than the page you were on. A prefix chain cannot
 * do better than that — the resolution it offers is the prefix, and the pages are
 * finer-grained than their prefixes.
 *
 * A table cannot fall through silently the way a prefix chain can, and
 * `page-titles.test.ts` reads `src/App.tsx` and fails when a route is added here
 * without a title — so the sweep stays swept.
 *
 * ## Matching
 *
 * Matched with React Router's own `matchRoutes`, not a hand-rolled loop, so ranking is
 * identical to the router that chose the page. That matters: `src/App.tsx` declares
 * `/ap-spend-files/:fileId` BEFORE `/ap-spend-files/upload`, and only the router's
 * static-beats-dynamic ranking sends `/ap-spend-files/upload` to the upload page. A
 * first-match-wins loop in declaration order would have titled it "AP Spend File".
 *
 * ## Redirects are routes too
 *
 * `DashboardLayoutRoute` wraps its `<Navigate>` routes, so the shell renders — and
 * resolves a title — for one commit before the redirect lands. `/tasks`, `/approvals`,
 * and the ~30 other redirect paths are therefore mapped to their DESTINATION's title,
 * which is both what the user is about to see and what stops the dev guard below from
 * throwing on a route that works perfectly.
 */

/** Resolves a title from the route's own path params. */
type TitleResolver = string | ((params: Readonly<Record<string, string | undefined>>) => string);

/**
 * Route pattern → title, in `src/App.tsx` declaration order.
 *
 * Titles are the text the page's own `<h1>`/`PageHeader` carried before this sweep
 * removed it, so the chrome inherits the name users already knew rather than a fresh
 * invention. Where a page rendered no title of its own, the sidebar nav label is used.
 * Pages whose title is DATA (a supplier's name, a campaign's name) keep a static route
 * label here and override it at runtime once loaded — see `usePageTitleOverride`.
 */
const PAGE_TITLES: ReadonlyArray<readonly [pattern: string, title: TitleResolver]> = [
  // --- Reports ------------------------------------------------------------
  ["/reports", "Programme Reports"],
  ["/proof", "Programme Reports"],
  ["/proof/:corporateId", "Programme Reports"],

  // --- Corporates ---------------------------------------------------------
  ["/corporates", "Corporate Registry"],
  ["/corporates/create", "Add New Corporate"],
  ["/reminders", "Corporate Registry"],
  ["/spend-files", "Corporate Registry"],

  // --- AP spend files (global) --------------------------------------------
  ["/ap-spend-files", "Corporate Registry"],
  ["/ap-spend-files/upload", "Upload AP Spend File"],
  ["/ap-spend-files/:fileId", "AP Spend File"],

  // --- Campaigns (global) -------------------------------------------------
  ["/campaigns/:campaignId", "Campaign"],
  ["/campaigns/:campaignId/add-suppliers", "Add Suppliers"],
  ["/campaign-strategy/:sessionId", "Campaign Strategy"],

  // --- Outreach -----------------------------------------------------------
  ["/enrollments", "Supplier Enrollments"],
  ["/approvals", "Supplier Enrollments"],
  ["/intelligence", "Shared Suppliers"],

  // --- Action Center ------------------------------------------------------
  ["/action-center", "Action Center"],
  ["/action-center/automations", "Action Center"],
  ["/tasks", "Action Center"],
  ["/rep-room", "Action Center"],
  ["/action-items", "Action Center"],

  // --- Workspaces ---------------------------------------------------------
  ["/strategy", "Strategy"],
  ["/supplier-onboarding", "Supplier Onboarding"],

  // --- Buyer onboarding ---------------------------------------------------
  ["/conversation-guide", "Treasury Guides"],
  ["/conversation-guide/new", "New Treasury Guide"],
  ["/conversation-guide/:guideId", "Treasury Guide"],

  // --- Admin --------------------------------------------------------------
  ["/admin", "Admin Settings"],
  ["/admin/mastra", "Admin Settings"],
  ["/admin/settings", "Admin Settings"],
  ["/negotiation", "Admin Settings"],
  ["/admin/settings/security", "Security Settings"],
  ["/account/security", "Security Settings"],
  ["/admin/users", "Users"],
  ["/admin/users/:userId", "User Details"],
  ["/admin/data-hygiene", "Data Hygiene Review"],
  ["/admin/audit-log", "Audit Log"],
  ["/admin/design-system", "Salt design system"],
  ["/admin/can-spam-readiness", "CAN-SPAM readiness"],
  ["/admin/crossover-suppliers", "Shared Suppliers"],
  ["/admin/negotiation-templates/create", "Create Negotiation Template"],
  ["/admin/negotiation-templates/:templateId/edit", "Edit Negotiation Template"],

  // --- Corporate workspace ------------------------------------------------
  // The corporate's NAME is not the title here: it is already the sidebar workspace
  // title, and one title per screen means this one answers "which page", not "which
  // customer". `/corporate/:id` is a bare redirect to `goals`, so it borrows that title.
  ["/corporate/:id", "Goals"],
  ["/corporate/:id/goals", "Goals"],
  ["/corporate/:id/goals/create", "Create Goal"],
  ["/corporate/:id/goals/:goalId", "Edit Goal"],
  ["/corporate/:id/overview", "Goals"],

  ["/corporate/:id/playbook", "Playbook"],
  ["/corporate/:id/reminders", "Call Reminders"],

  ["/corporate/:id/proposal", "Proposal"],
  ["/corporate/:id/proposal/planning", "Planning"],
  ["/corporate/:id/proposal/proposal", "Proposal"],
  ["/corporate/:id/proposal/opportunity-map", "Planning"],
  ["/corporate/:id/proposal/strategy-planner", "Proposal"],
  ["/corporate/:id/proposal/scenario-lab", "Planning"],
  ["/corporate/:id/proposal/cohorts", "Planning"],
  ["/corporate/:id/proposal/decision-brief", "Planning"],
  ["/corporate/:id/proposal/proof", "Planning"],
  ["/corporate/:id/proposal/decision-council", "Planning"],
  ["/corporate/:id/proposal/activation-plan", "Planning"],
  ["/corporate/:id/cohorts", "Planning"],
  ["/corporate/:id/activity", "Planning"],
  ["/corporate/:id/documents", "Planning"],

  ["/corporate/:id/contacts", "Contacts"],
  ["/corporate/:id/contacts/create", "Add New Contact"],
  ["/corporate/:id/contacts/:contactId", "Contact Details"],

  ["/corporate/:id/vendors", "Vendors"],
  ["/corporate/:id/vendors/create", "Add Vendor"],
  ["/corporate/:id/vendors/:profileId", "Vendor Details"],
  ["/corporate/:id/trade-profiles", "Vendors"],
  ["/corporate/:id/leads/new", "Capture Lead"],

  ["/corporate/:id/campaigns", "Campaigns"],
  ["/corporate/:id/campaigns/create", "Create New Campaign"],
  ["/corporate/:id/files", "AP files"],
  ["/corporate/:id/ap-spend-files", "AP files"],
  ["/corporate/:id/ap-spend-files/upload", "Upload AP Spend File"],
  ["/corporate/:id/ap-spend-files/:fileId", "AP Spend File"],

  ["/corporate/:id/email-studio", "Email Studio"],
  ["/corporate/:id/templates", "Templates"],
  ["/corporate/:id/compliance-center", "Corporate Details"],
  ["/corporate/:id/supplier-onboarding", "Supplier onboarding"],
  ["/corporate/:id/supplier-onboarding/:packageId", "Onboarding package"],

  ["/corporate/:id/policies", "Policies"],
  ["/corporate/:id/strategy", "Policies"],
  ["/corporate/:id/policies/configurations/create", "Create Strategy Configuration"],
  ["/corporate/:id/policies/templates/create", "Create Negotiation Template"],
  ["/corporate/:id/strategy/session/:sessionId", "Campaign Strategy"],

  ["/corporate/:id/settings", "Settings"],
  ["/corporate/:id/details", "Corporate Details"],
  ["/corporate/:id/financial-settings", "Corporate Details"],
  ["/corporate/:id/uploadfinancialreports", "Upload financial reports"],
];

/** Shown instead of crashing a customer's browser when a route slips through in production. */
export const APP_NAME = "B2B Activate";

/** Every mapped pattern, for the coverage test to diff against `src/App.tsx`. */
export const MAPPED_TITLE_PATTERNS: readonly string[] = PAGE_TITLES.map(([pattern]) => pattern);

const TITLE_ROUTES = PAGE_TITLES.map(([path]) => ({ path }));

/**
 * The chrome title for `pathname`, or `null` when the route is not mapped.
 *
 * Returning `null` rather than a fallback keeps the "unmapped" case visible to the
 * caller, which is what lets `getPageTitle` fail loudly in dev and degrade quietly in
 * production instead of having to guess which it is looking at.
 */
export function resolvePageTitle(pathname: string): string | null {
  const matches = matchRoutes(TITLE_ROUTES, pathname);
  if (!matches || matches.length === 0) return null;

  const match = matches[matches.length - 1];
  const index = TITLE_ROUTES.indexOf(match.route);
  if (index < 0) return null;

  const [, title] = PAGE_TITLES[index];
  return typeof title === "function" ? title(match.params) : title;
}
